import { Navigate, Route, Routes } from 'react-router-dom'

import { Layout } from './components/Layout'
import { AppSkeleton } from './components/Skeleton'
import { useStore } from './lib/store'
import { ActivityFeed } from './routes/ActivityFeed'
import { BoardPage } from './routes/BoardPage'
import { ClientPage } from './routes/ClientPage'
import { DealsPage } from './routes/DealsPage'
import { DuePage } from './routes/DuePage'
import { EntityDetail } from './routes/EntityDetail'
import { EntityListPage } from './routes/EntityListPage'
import { NetworkPage } from './routes/NetworkPage'
import { Overview } from './routes/Overview'
import { ProjectsPage } from './routes/ProjectsPage'
import { SettingsPage } from './routes/SettingsPage'
import { SetupPage } from './routes/SetupPage'
import { StagingPage } from './routes/StagingPage'

export function App() {
  const { ready, brand } = useStore()
  if (!ready) return <AppSkeleton />

  // A deployment that has never been named gets the setup prompt first. It is a
  // redirect rather than a gate: /setup can be skipped, and once a name is saved
  // the condition is false forever.
  const unnamed = !brand.instanceName.trim()

  return (
    <Routes>
      <Route path="/setup" element={<SetupPage />} />
      <Route element={<Layout />}>
        {/* Overview is the landing: the lists answer "find me X", and none of them
            answers "what wants me today", which is the question you open the app
            with. Clients stays one tab away — see views.ts. */}
        <Route index element={<Navigate to={unnamed ? '/setup' : '/overview'} replace />} />
        <Route path="/overview" element={<Overview />} />
        <Route path="/c/:orgId" element={<ClientPage />} />
        <Route path="/b/:workId" element={<BoardPage />} />
        <Route path="/due" element={<DuePage />} />
        <Route path="/staging" element={<StagingPage />} />
        <Route path="/deals" element={<DealsPage />} />
        {/* A lens is a filter over the one work table, not a separate list. */}
        <Route path="/work" element={<ProjectsPage />} />
        <Route path="/artifacts" element={<EntityListPage kind="artifact" />} />
        {/* Work has no state any more, so the old set-aside lens is just the list.
            This has to precede `/work/:lens`, which would otherwise match it. */}
        <Route path="/work/inactive" element={<Navigate to="/work" replace />} />
        <Route path="/work/:lens" element={<ProjectsPage />} />
        <Route path="/orgs" element={<EntityListPage kind="org" />} />
        <Route path="/people" element={<EntityListPage kind="person" />} />
        <Route path="/e/:id" element={<EntityDetail />} />
        <Route path="/network" element={<NetworkPage />} />
        <Route path="/activity" element={<ActivityFeed />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* Older routes, kept so existing bookmarks and deep links still land. */}
        <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
        <Route path="/loose-ends" element={<Navigate to="/due" replace />} />
        {/* Import moved into Settings, so the rail stays clear. */}
        <Route path="/import" element={<Navigate to="/settings" replace />} />
        <Route path="/projects" element={<Navigate to="/work" replace />} />
        <Route path="/projects/:lens" element={<Navigate to="/work" replace />} />
        {/* Tags had no use yet, so the surface is gone; the events still fold. */}
        {/* "Lineage" then "Impact": both were the same graph under a narrower name. */}
        <Route path="/lineage" element={<Navigate to="/network" replace />} />
        <Route path="/impact" element={<Navigate to="/network" replace />} />
        <Route path="/tags" element={<Navigate to="/overview" replace />} />
        <Route path="/tags/:label" element={<Navigate to="/overview" replace />} />
        <Route path="*" element={<Navigate to="/orgs" replace />} />
      </Route>
    </Routes>
  )
}
